import React, { useRef, useEffect, useState } from 'react';
import { sanitizeHTML } from '../../utils/sanitize';

const EnhancedRichTextEditor = ({ value, onChange, placeholder, className = '' }) => {
  const editorRef = useRef(null);
  const [selectedFont, setSelectedFont] = useState('Arial');
  const [selectedSize, setSelectedSize] = useState('12');
  const [selectedColor, setSelectedColor] = useState('#000000');
  const [selectedBgColor, setSelectedBgColor] = useState('#ffffff');
  const [isHtmlMode, setIsHtmlMode] = useState(false);
  const [htmlContent, setHtmlContent] = useState('');
  const [showHtmlDialog, setShowHtmlDialog] = useState(false);
  const [showCodeDialog, setShowCodeDialog] = useState(false);
  const [showLinkDialog, setShowLinkDialog] = useState(false);
  const [selectedLinkText, setSelectedLinkText] = useState('');

  useEffect(() => {
    if (editorRef.current && value !== editorRef.current.innerHTML) {
      // Sanitize content before setting
      const sanitizedValue = sanitizeHTML(value || '', {
        ALLOWED_TAGS: ['p', 'br', 'strong', 'b', 'em', 'i', 'u', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'ul', 'ol', 'li', 'a', 'img', 'blockquote', 'code', 'pre', 'span', 'div'],
        ALLOWED_ATTR: ['href', 'title', 'target', 'src', 'alt', 'style', 'class']
      });
      
      // Save scroll position
      const scrollTop = editorRef.current.scrollTop;
      
      editorRef.current.innerHTML = sanitizedValue;
      
      // Restore scroll position
      editorRef.current.scrollTop = scrollTop;
    }
  }, [value]);
  
  // Prevent default browser behavior on first click
  useEffect(() => {
    const handleFirstClick = (e) => {
      if (editorRef.current && editorRef.current.contains(e.target)) {
        // Ensure the editor stays focused at the click position
        const selection = window.getSelection();
        if (selection.rangeCount === 0) {
          const range = document.createRange();
          range.selectNodeContents(editorRef.current);
          range.collapse(true);
          selection.removeAllRanges();
          selection.addRange(range);
        }
      }
    };
    
    document.addEventListener('mousedown', handleFirstClick);
    return () => document.removeEventListener('mousedown', handleFirstClick);
  }, []);

  const handleInput = () => {
    if (editorRef.current) {
      // Sanitize content before passing to parent
      const sanitizedContent = sanitizeHTML(editorRef.current.innerHTML, {
        ALLOWED_TAGS: ['p', 'br', 'strong', 'b', 'em', 'i', 'u', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'ul', 'ol', 'li', 'a', 'img', 'blockquote', 'code', 'pre', 'span', 'div'],
        ALLOWED_ATTR: ['href', 'title', 'target', 'src', 'alt', 'style', 'class']
      });
      onChange(sanitizedContent);
    }
  };

  const formatText = (command, value = null) => {
    try {
      // Ensure we have focus and selection
      editorRef.current.focus();
      
      // For some browsers, we need to ensure the editor is focused before execCommand
      setTimeout(() => {
        const success = document.execCommand(command, false, value);
        if (!success) {
          console.warn(`Command ${command} failed, trying alternative method`);
          // Add fallback methods for critical commands
          handleFallbackFormatting(command, value);
        }
        handleInput();
      }, 10);
    } catch (error) {
      console.warn(`execCommand failed for ${command}:`, error);
      handleFallbackFormatting(command, value);
      handleInput();
    }
  };

  const handleFallbackFormatting = (command, value) => {
    const selection = window.getSelection();
    if (selection.rangeCount === 0) return;
    
    const range = selection.getRangeAt(0);
    const selectedText = selection.toString();
    
    switch (command) {
      case 'bold':
        if (selectedText) {
          const bold = document.createElement('strong');
          bold.textContent = selectedText;
          range.deleteContents();
          range.insertNode(bold);
        }
        break;
      case 'italic':
        if (selectedText) {
          const italic = document.createElement('em');
          italic.textContent = selectedText;
          range.deleteContents();
          range.insertNode(italic);
        }
        break;
      case 'underline':
        if (selectedText) {
          const underline = document.createElement('u');
          underline.textContent = selectedText;
          range.deleteContents();
          range.insertNode(underline);
        }
        break;
      default:
        // For other commands, we'll still rely on execCommand
        break;
    }
  };

  const insertList = (type) => {
    // Ensure editor is focused first
    editorRef.current.focus();
    
    const selection = window.getSelection();
    if (selection.rangeCount === 0) {
      // Create a range at the end of the editor if no selection
      const range = document.createRange();
      range.selectNodeContents(editorRef.current);
      range.collapse(false);
      selection.removeAllRanges();
      selection.addRange(range);
    }
    
    const range = selection.getRangeAt(0);
    
    // Check if we're already in a list
    let listElement = null;
    let currentNode = range.commonAncestorContainer;
    
    // Walk up the DOM tree to find any list element
    while (currentNode && currentNode !== editorRef.current) {
      if (currentNode.nodeType === Node.ELEMENT_NODE && (currentNode.tagName === 'UL' || currentNode.tagName === 'OL')) {
        listElement = currentNode;
        break;
      }
      currentNode = currentNode.parentNode;
    }
    
    if (listElement) {
      // If we're in a list, try to toggle it off
      const listItems = listElement.querySelectorAll('li');
      const paragraphs = Array.from(listItems).map(li => {
        const p = document.createElement('p');
        p.innerHTML = li.innerHTML;
        return p;
      });
      
      // Replace the list with paragraphs
      paragraphs.forEach((p, index) => {
        if (index === 0) {
          listElement.parentNode.insertBefore(p, listElement);
        } else {
          listElement.parentNode.insertBefore(p, listElement);
        }
      });
      listElement.parentNode.removeChild(listElement);
    } else {
      // Create new list
      const listTag = type === 'ol' ? 'ol' : 'ul';
      const selectedText = selection.toString();
      
      if (selectedText) {
        // If there's selected text, wrap it in a list
        const listElement = document.createElement(listTag);
        const listItem = document.createElement('li');
        listItem.textContent = selectedText;
        listElement.appendChild(listItem);
        
        try {
          range.deleteContents();
          range.insertNode(listElement);
          
          // Position cursor at the end of the list item
          const newRange = document.createRange();
          newRange.setStart(listItem, listItem.childNodes.length);
          newRange.collapse(true);
          selection.removeAllRanges();
          selection.addRange(newRange);
        } catch (e) {
          console.warn('Range manipulation failed, using innerHTML method');
          const listHtml = `<${listTag}><li>${selectedText}</li></${listTag}>`;
          document.execCommand('insertHTML', false, listHtml);
        }
      } else {
        // If no selection, create a list with an empty item
        const listHtml = `<${listTag}><li>List item</li></${listTag}>`;
        try {
          document.execCommand('insertHTML', false, listHtml);
        } catch (e) {
          const listElement = document.createElement(listTag);
          const listItem = document.createElement('li');
          listItem.textContent = 'List item';
          listElement.appendChild(listItem);
          
          range.insertNode(listElement);
          
          // Position cursor in the list item
          const newRange = document.createRange();
          newRange.selectNodeContents(listItem);
          selection.removeAllRanges();
          selection.addRange(newRange);
        }
      }
    }
    
    // Ensure focus is maintained
    setTimeout(() => {
      editorRef.current.focus();
      handleInput();
    }, 50);
  };

  const insertTable = () => {
    const rows = prompt('Number of rows:', '3');
    const cols = prompt('Number of columns:', '3');
    if (rows && cols) {
      let table = '<table border="1" style="border-collapse: collapse; width: 100%; margin: 10px 0;">';
      for (let i = 0; i < rows; i++) {
        table += '<tr>';
        for (let j = 0; j < cols; j++) {
          table += '<td style="padding: 8px; border: 1px solid #ccc;">Cell</td>';
        }
        table += '</tr>';
      }
      table += '</table>';
      formatText('insertHTML', table);
    }
  };

  const insertLink = () => {
    const selection = window.getSelection();
    const selectedText = selection.toString();
    setSelectedLinkText(selectedText || '');
    setShowLinkDialog(true);
  };

  const handleInsertLink = () => {
    const urlInput = document.getElementById('link-url-input');
    const textInput = document.getElementById('link-text-input');
    const url = urlInput?.value;
    const text = textInput?.value;
    
    if (url && url.trim() && text && text.trim()) {
      const fullUrl = url.startsWith('http') ? url : `https://${url}`;
      formatText('insertHTML', `<a href="${fullUrl}" target="_blank" style="color: #1e40af; text-decoration: underline;">${text}</a>`);
      setShowLinkDialog(false);
      urlInput.value = '';
      textInput.value = '';
      setSelectedLinkText('');
    }
  };

  const insertImage = () => {
    const url = prompt('Enter image URL:');
    if (url) {
      formatText('insertImage', url);
    }
  };

  const changeFontFamily = (font) => {
    setSelectedFont(font);
    formatText('fontName', font);
  };

  const changeFontSize = (size) => {
    setSelectedSize(size);
    formatText('fontSize', size);
  };

  const changeTextColor = (color) => {
    setSelectedColor(color);
    formatText('foreColor', color);
  };

  const changeBackgroundColor = (color) => {
    setSelectedBgColor(color);
    formatText('backColor', color);
  };

  const insertHorizontalRule = () => {
    formatText('insertHorizontalRule');
  };

  const insertSpecialChar = (char) => {
    formatText('insertText', char);
  };

  const handlePaste = (e) => {
    e.preventDefault();
    
    // Save current selection/cursor position
    const selection = window.getSelection();
    const range = selection.rangeCount > 0 ? selection.getRangeAt(0) : null;
    
    const html = e.clipboardData.getData('text/html');
    const text = e.clipboardData.getData('text/plain');
    
    // Focus the editor to ensure we have a valid selection
    editorRef.current.focus();
    
    // Restore the saved range if we had one
    if (range) {
      selection.removeAllRanges();
      selection.addRange(range);
    }
    
    try {
      // If HTML content is available and looks like actual HTML, use it
      if (html && html.includes('<') && html.includes('>')) {
        // Clean up MS Word formatting
        let cleanHtml = html
          .replace(/<!--[\s\S]*?-->/g, '') // Remove comments
          .replace(/mso-[^:;"]*/gi, '') // Remove MSO styles
          .replace(/<h1(\s|>)/gi, '<p$1') // Convert h1 to p
          .replace(/<\/h1>/gi, '</p>')
          .replace(/<h2(\s|>)/gi, '<p$1') // Convert h2 to p
          .replace(/<\/h2>/gi, '</p>')
          .replace(/\s*class="Mso[^"]*"/gi, '') // Remove Word classes
          .replace(/style="[^"]*"/gi, (match) => {
            // Keep only essential styles
            const allowedStyles = ['text-align', 'font-weight', 'font-style', 'text-decoration'];
            const styleObj = {};
            match.replace(/style="([^"]*)"/gi, (_, styles) => {
              styles.split(';').forEach(style => {
                const [prop, value] = style.split(':').map(s => s.trim());
                if (prop && value && allowedStyles.some(allowed => prop.includes(allowed))) {
                  styleObj[prop] = value;
                }
              });
            });
            const newStyles = Object.entries(styleObj).map(([k, v]) => `${k}: ${v}`).join('; ');
            return newStyles ? `style="${newStyles}"` : '';
          });
        
        document.execCommand('insertHTML', false, cleanHtml);
      } else {
        // For plain text, convert line breaks to paragraphs
        const paragraphs = text.split(/\n\n+/).filter(p => p.trim());
        const formattedText = paragraphs.map(p => `<p>${p.replace(/\n/g, '<br>')}</p>`).join('');
        document.execCommand('insertHTML', false, formattedText || text);
      }
      
      // Ensure cursor stays at the end of pasted content
      setTimeout(() => {
        const sel = window.getSelection();
        if (sel.rangeCount > 0) {
          const range = sel.getRangeAt(0);
          range.collapse(false);
          sel.removeAllRanges();
          sel.addRange(range);
        }
        handleInput();
      }, 10);
    } catch (error) {
      console.error('Paste error:', error);
      // Fallback to simple text insertion
      document.execCommand('insertText', false, text);
      handleInput();
    }
  };

  const toggleHtmlMode = () => {
    if (isHtmlMode) {
      // Switching from HTML to visual mode
      if (editorRef.current) {
        editorRef.current.innerHTML = htmlContent;
        handleInput();
      }
    } else {
      // Switching from visual to HTML mode
      if (editorRef.current) {
        setHtmlContent(editorRef.current.innerHTML);
      }
    }
    setIsHtmlMode(!isHtmlMode);
  };

  const insertCodeBlock = () => {
    setShowCodeDialog(true);
  };

  const handleInsertCodeBlock = () => {
    const codeInput = document.getElementById('code-block-input');
    const code = codeInput?.value;
    if (code && code.trim()) {
      const codeHtml = `<pre style="background: #f4f4f4; padding: 10px; border-radius: 4px; border-left: 3px solid #007cba; overflow-x: auto; font-family: 'Courier New', monospace; font-size: 14px;"><code>${code.replace(/</g, '&lt;').replace(/>/g, '&gt;')}</code></pre>`;
      formatText('insertHTML', codeHtml);
      setShowCodeDialog(false);
      codeInput.value = '';
    }
  };

  const showHtmlPasteDialog = () => {
    setShowHtmlDialog(true);
  };

  const insertHtmlCode = () => {
    const htmlCode = document.getElementById('html-input').value;
    if (htmlCode.trim()) {
      formatText('insertHTML', htmlCode);
      setShowHtmlDialog(false);
      document.getElementById('html-input').value = '';
    }
  };

  const formatAsCode = () => {
    const selection = window.getSelection();
    if (selection.toString()) {
      const selectedText = selection.toString();
      const codeHtml = `<code style="background: #f1f1f1; padding: 2px 4px; border-radius: 3px; font-family: 'Courier New', monospace; color: #d63384;">${selectedText}</code>`;
      formatText('insertHTML', codeHtml);
    } else {
      alert('Please select text first to format as inline code');
    }
  };

  const toggleFullScreen = () => {
    const container = editorRef.current.closest('.rich-editor-container');
    if (container.classList.contains('fullscreen')) {
      container.classList.remove('fullscreen');
    } else {
      container.classList.add('fullscreen');
    }
  };

  return (
    <div className={`rich-editor-container border border-border_gray rounded-lg ${className}`}>
      <style jsx>{`
        .fullscreen {
          position: fixed !important;
          top: 0 !important;
          left: 0 !important;
          width: 100vw !important;
          height: 100vh !important;
          z-index: 9999 !important;
          background: white !important;
        }
        .fullscreen .editor-content {
          height: calc(100vh - 120px) !important;
        }
        .editor-content {
          scroll-behavior: smooth;
          position: relative;
        }
        .editor-content:focus {
          outline: none;
        }
        .editor-content p {
          margin: 0 0 1em 0;
        }
        .editor-content p:last-child {
          margin-bottom: 0;
        }
        .editor-content ul, .editor-content ol {
          margin: 10px 0 !important;
          padding-left: 30px !important;
        }
        .editor-content ul li, .editor-content ol li {
          margin: 5px 0 !important;
          list-style-position: outside !important;
        }
        .editor-content ul li {
          list-style-type: disc !important;
        }
        .editor-content ol li {
          list-style-type: decimal !important;
        }
        .editor-content ul ul li {
          list-style-type: circle !important;
        }
        .editor-content ol ol li {
          list-style-type: lower-alpha !important;
        }
        /* Prevent jumping on paste */
        .editor-content[contenteditable]:focus {
          overflow-y: auto;
        }
      `}</style>

      {/* Compact Ribbon Toolbar */}
      <div className="border-b border-border_gray bg-gradient-to-r from-gray-50 to-gray-100">
        {/* Main Ribbon - Single Row with Organized Groups */}
        <div className="flex flex-wrap items-center gap-1 p-3">
          
          {/* Font & Size Group */}
          <div className="flex items-center gap-2 bg-white rounded-lg p-2 shadow-sm border border-gray-200">
            <select
              value={selectedFont}
              onChange={(e) => changeFontFamily(e.target.value)}
              className="text-sm border-0 bg-transparent focus:outline-none w-24"
              title="Font Family"
            >
              <option value="Arial">Arial</option>
              <option value="Times New Roman">Times</option>
              <option value="Georgia">Georgia</option>
              <option value="Verdana">Verdana</option>
              <option value="Helvetica">Helvetica</option>
            </select>
            <div className="w-px bg-gray-300 h-4"></div>
            <select
              value={selectedSize}
              onChange={(e) => changeFontSize(e.target.value)}
              className="text-sm border-0 bg-transparent focus:outline-none w-12"
              title="Font Size"
            >
              <option value="1">8</option>
              <option value="2">10</option>
              <option value="3">12</option>
              <option value="4">14</option>
              <option value="5">18</option>
              <option value="6">24</option>
              <option value="7">36</option>
            </select>
          </div>

          {/* Text Formatting Group */}
          <div className="flex items-center bg-white rounded-lg p-1 shadow-sm border border-gray-200">
            <button
              type="button"
              onClick={() => formatText('bold')}
              className="w-8 h-8 flex items-center justify-center rounded hover:bg-blue-100 hover:text-blue-600 transition-colors"
              title="Bold (Ctrl+B)"
            >
              <i className="fas fa-bold text-sm"></i>
            </button>
            <button
              type="button"
              onClick={() => formatText('italic')}
              className="w-8 h-8 flex items-center justify-center rounded hover:bg-blue-100 hover:text-blue-600 transition-colors"
              title="Italic (Ctrl+I)"
            >
              <i className="fas fa-italic text-sm"></i>
            </button>
            <button
              type="button"
              onClick={() => formatText('underline')}
              className="w-8 h-8 flex items-center justify-center rounded hover:bg-blue-100 hover:text-blue-600 transition-colors"
              title="Underline (Ctrl+U)"
            >
              <i className="fas fa-underline text-sm"></i>
            </button>
            <div className="w-px bg-gray-300 h-4 mx-1"></div>
            <div className="flex items-center gap-1">
              <input
                type="color"
                value={selectedColor}
                onChange={(e) => changeTextColor(e.target.value)}
                className="w-6 h-6 border-0 rounded cursor-pointer"
                title="Text Color"
              />
              <input
                type="color"
                value={selectedBgColor}
                onChange={(e) => changeBackgroundColor(e.target.value)}
                className="w-6 h-6 border-0 rounded cursor-pointer"
                title="Highlight"
              />
            </div>
          </div>

          {/* Alignment & Lists Group */}
          <div className="flex items-center bg-white rounded-lg p-1 shadow-sm border border-gray-200">
            <button
              type="button"
              onClick={() => formatText('justifyLeft')}
              className="w-8 h-8 flex items-center justify-center rounded hover:bg-green-100 hover:text-green-600 transition-colors"
              title="Align Left"
            >
              <i className="fas fa-align-left text-sm"></i>
            </button>
            <button
              type="button"
              onClick={() => formatText('justifyCenter')}
              className="w-8 h-8 flex items-center justify-center rounded hover:bg-green-100 hover:text-green-600 transition-colors"
              title="Center"
            >
              <i className="fas fa-align-center text-sm"></i>
            </button>
            <button
              type="button"
              onClick={() => formatText('justifyRight')}
              className="w-8 h-8 flex items-center justify-center rounded hover:bg-green-100 hover:text-green-600 transition-colors"
              title="Align Right"
            >
              <i className="fas fa-align-right text-sm"></i>
            </button>
            <button
              type="button"
              onClick={() => formatText('justifyFull')}
              className="w-8 h-8 flex items-center justify-center rounded hover:bg-green-100 hover:text-green-600 transition-colors"
              title="Justify"
            >
              <i className="fas fa-align-justify text-sm"></i>
            </button>
            <div className="w-px bg-gray-300 h-4 mx-1"></div>
            <button
              type="button"
              onClick={() => insertList('ul')}
              className="w-8 h-8 flex items-center justify-center rounded hover:bg-green-100 hover:text-green-600 transition-colors"
              title="Bullet List"
            >
              <i className="fas fa-list-ul text-sm"></i>
            </button>
            <button
              type="button"
              onClick={() => insertList('ol')}
              className="w-8 h-8 flex items-center justify-center rounded hover:bg-green-100 hover:text-green-600 transition-colors"
              title="Numbered List"
            >
              <i className="fas fa-list-ol text-sm"></i>
            </button>
          </div>

          {/* Format & Structure Group */}
          <div className="flex items-center gap-2 bg-white rounded-lg p-2 shadow-sm border border-gray-200">
            <select
              onChange={(e) => {
                if (e.target.value) {
                  formatText('formatBlock', `<${e.target.value}>`);
                  e.target.value = '';
                }
              }}
              className="text-sm border-0 bg-transparent focus:outline-none w-20"
              defaultValue=""
              title="Text Format"
            >
              <option value="">Format</option>
              <option value="h1">H1</option>
              <option value="h2">H2</option>
              <option value="h3">H3</option>
              <option value="p">Normal</option>
              <option value="blockquote">Quote</option>
            </select>
            <div className="w-px bg-gray-300 h-4"></div>
            <button
              type="button"
              onClick={() => formatText('indent')}
              className="w-6 h-6 flex items-center justify-center rounded hover:bg-purple-100 hover:text-purple-600 transition-colors"
              title="Indent"
            >
              <i className="fas fa-indent text-xs"></i>
            </button>
            <button
              type="button"
              onClick={() => formatText('outdent')}
              className="w-6 h-6 flex items-center justify-center rounded hover:bg-purple-100 hover:text-purple-600 transition-colors"
              title="Outdent"
            >
              <i className="fas fa-outdent text-xs"></i>
            </button>
          </div>

          {/* Insert Group */}
          <div className="flex items-center bg-white rounded-lg p-1 shadow-sm border border-gray-200">
            <button
              type="button"
              onClick={insertLink}
              className="w-8 h-8 flex items-center justify-center rounded hover:bg-orange-100 hover:text-orange-600 transition-colors"
              title="Insert Link"
            >
              <i className="fas fa-link text-sm"></i>
            </button>
            <button
              type="button"
              onClick={insertImage}
              className="w-8 h-8 flex items-center justify-center rounded hover:bg-orange-100 hover:text-orange-600 transition-colors"
              title="Insert Image"
            >
              <i className="fas fa-image text-sm"></i>
            </button>
            <button
              type="button"
              onClick={insertTable}
              className="w-8 h-8 flex items-center justify-center rounded hover:bg-orange-100 hover:text-orange-600 transition-colors"
              title="Insert Table"
            >
              <i className="fas fa-table text-sm"></i>
            </button>
            <button
              type="button"
              onClick={insertHorizontalRule}
              className="w-8 h-8 flex items-center justify-center rounded hover:bg-orange-100 hover:text-orange-600 transition-colors"
              title="Insert Line"
            >
              <i className="fas fa-minus text-sm"></i>
            </button>
          </div>

          {/* Code & HTML Group */}
          <div className="flex items-center bg-white rounded-lg p-1 shadow-sm border border-gray-200">
            <button
              type="button"
              onClick={insertCodeBlock}
              className="w-8 h-8 flex items-center justify-center rounded hover:bg-indigo-100 hover:text-indigo-600 transition-colors"
              title="Insert Code Block"
            >
              <i className="fas fa-code text-sm"></i>
            </button>
            <button
              type="button"
              onClick={formatAsCode}
              className="w-8 h-8 flex items-center justify-center rounded hover:bg-indigo-100 hover:text-indigo-600 transition-colors"
              title="Format as Inline Code"
            >
              <i className="fas fa-terminal text-sm"></i>
            </button>
            <button
              type="button"
              onClick={showHtmlPasteDialog}
              className="w-8 h-8 flex items-center justify-center rounded hover:bg-indigo-100 hover:text-indigo-600 transition-colors"
              title="Paste HTML Code"
            >
              <i className="fab fa-html5 text-sm"></i>
            </button>
            <button
              type="button"
              onClick={toggleHtmlMode}
              className={`w-8 h-8 flex items-center justify-center rounded transition-colors ${
                isHtmlMode 
                  ? 'bg-indigo-200 text-indigo-700' 
                  : 'hover:bg-indigo-100 hover:text-indigo-600'
              }`}
              title="Toggle HTML Source View"
            >
              <i className="fas fa-file-code text-sm"></i>
            </button>
          </div>

          {/* Actions Group */}
          <div className="flex items-center bg-white rounded-lg p-1 shadow-sm border border-gray-200">
            <button
              type="button"
              onClick={() => formatText('undo')}
              className="w-8 h-8 flex items-center justify-center rounded hover:bg-gray-100 transition-colors"
              title="Undo (Ctrl+Z)"
            >
              <i className="fas fa-undo text-sm"></i>
            </button>
            <button
              type="button"
              onClick={() => formatText('redo')}
              className="w-8 h-8 flex items-center justify-center rounded hover:bg-gray-100 transition-colors"
              title="Redo (Ctrl+Y)"
            >
              <i className="fas fa-redo text-sm"></i>
            </button>
            <div className="w-px bg-gray-300 h-4 mx-1"></div>
            <button
              type="button"
              onClick={() => formatText('removeFormat')}
              className="w-8 h-8 flex items-center justify-center rounded hover:bg-red-100 hover:text-red-600 transition-colors"
              title="Clear Formatting"
            >
              <i className="fas fa-eraser text-sm"></i>
            </button>
            <button
              type="button"
              onClick={toggleFullScreen}
              className="w-8 h-8 flex items-center justify-center rounded hover:bg-blue-100 hover:text-blue-600 transition-colors"
              title="Full Screen"
            >
              <i className="fas fa-expand text-sm"></i>
            </button>
          </div>

          {/* Special Characters - Compact */}
          <div className="flex items-center bg-white rounded-lg px-2 py-1 shadow-sm border border-gray-200">
            <span className="text-xs text-gray-500 mr-1">±</span>
            {['©', '®', '™', '€'].map(char => (
              <button
                key={char}
                type="button"
                onClick={() => insertSpecialChar(char)}
                className="w-6 h-6 text-xs flex items-center justify-center rounded hover:bg-gray-100 transition-colors"
                title={`Insert ${char}`}
              >
                {char}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Editor */}
      <div
        ref={editorRef}
        contentEditable
        onInput={handleInput}
        onPaste={handlePaste}
        className="editor-content p-4 min-h-[300px] max-h-[600px] overflow-y-auto focus:outline-none"
        style={{ whiteSpace: 'pre-wrap' }}
        suppressContentEditableWarning={true}
        placeholder={placeholder}
      />

      {/* Status Bar */}
      <div className="border-t border-border_gray px-4 py-2 bg-gray-50 flex justify-between items-center text-xs text-gray-500">
        <div>
          Characters: {value ? value.replace(/<[^>]*>/g, '').length : 0} | 
          Words: {value ? value.replace(/<[^>]*>/g, '').split(/\s+/).filter(w => w.length > 0).length : 0}
        </div>
        <div>
          Tips: Use Ctrl+B for bold, Ctrl+I for italic, Ctrl+Z for undo
        </div>
      </div>

      {/* Link Dialog */}
      {showLinkDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-96 max-w-90vw">
            <h3 className="text-lg font-semibold mb-4">Insert Link</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Link Text (what users will see)</label>
                <input
                  id="link-text-input"
                  type="text"
                  defaultValue={selectedLinkText}
                  className="w-full border border-gray-300 rounded p-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Hello"
                  autoFocus
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">URL (where the link goes)</label>
                <input
                  id="link-url-input"
                  type="url"
                  className="w-full border border-gray-300 rounded p-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="https://example.com"
                />
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <button
                type="button"
                onClick={() => setShowLinkDialog(false)}
                className="px-4 py-2 border border-gray-300 rounded hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleInsertLink}
                className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
              >
                Insert Link
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Code Block Dialog */}
      {showCodeDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-96 max-w-90vw">
            <h3 className="text-lg font-semibold mb-4">Insert Code Block</h3>
            <textarea
              id="code-block-input"
              className="w-full h-32 border border-gray-300 rounded p-3 font-mono text-sm resize-none focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Enter your code here..."
              autoFocus
            />
            <div className="flex justify-end gap-2 mt-4">
              <button
                type="button"
                onClick={() => setShowCodeDialog(false)}
                className="px-4 py-2 border border-gray-300 rounded hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleInsertCodeBlock}
                className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
              >
                Insert Code
              </button>
            </div>
          </div>
        </div>
      )}

      {/* HTML Dialog */}
      {showHtmlDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-96 max-w-90vw">
            <h3 className="text-lg font-semibold mb-4">Insert HTML Code</h3>
            <textarea
              id="html-input"
              className="w-full h-32 border border-gray-300 rounded p-3 font-mono text-sm resize-none focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Enter HTML code here..."
              autoFocus
            />
            <div className="flex justify-end gap-2 mt-4">
              <button
                type="button"
                onClick={() => setShowHtmlDialog(false)}
                className="px-4 py-2 border border-gray-300 rounded hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={insertHtmlCode}
                className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
              >
                Insert HTML
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EnhancedRichTextEditor;